import React from 'react';
import { ActiveLink } from '@components/common/navigation/ActiveNavLink';
import { Permissions, Roles, useUserPermissions } from '@hooks/user/useUserPermissions';
import { SidebarEditor, SideMenuEditContext } from '../editSidebar/SidebarEditor';
import { motion } from 'framer-motion';
import { Text } from '@skipper/ui';
import NextLink from 'next/link';

const ManagedLinks = [
    {
        name: 'Profile',
        route: '/users/edit',
        permissionCheck: Permissions.manage_profile,
    },
    { name: 'Manage Family', route: '/account/seats', roleCheck: Roles.Adult },
    { name: 'My Enrollments', route: '/account/enrollments' },
    {
        name: 'Manage Courses',
        route: '/account/manage-active',
        roleCheck: Roles.Adult,
    },
    {
        name: 'Manage Members',
        route: '/account/manage-members',
        roleCheck: Roles.Adult,
    },
    {
        name: 'Notifications',
        route: '/users/notifications',
        permissionCheck: Permissions.notification_settings,
    },
    {
        name: 'Account Settings',
        route: '/account/settings',
        roleCheck: Roles.Adult,
    },
    {
        name: 'Billing and Plans',
        route: '/account/billing',
        roleCheck: Roles.Adult,
    },
    {
        name: 'Permission Levels',
        route: '/account/permission-levels',
        roleCheck: Roles.Adult,
    },
];

export function LeftsideNav({
    children,
    bgColor = 'bg-white',
    subHeaderContent,
    breadCrumb,
    breadCrumbUrl,
    breadCrumSub,
    breadCrumSubUrl,
}: {
    children: React.ReactNode;
    bgColor?: string;
    subHeaderContent?: React.ReactNode;
    breadCrumb?: string;
    breadCrumbUrl?: string;
    breadCrumSub?: string;
    breadCrumSubUrl?: string;
}) {
    const { hasPermission, hasRole } = useUserPermissions();

    return (
        <div className="h-full w-full">
            <div className={`relative h-full w-full ${bgColor} pr-14`}>
                <motion.div
                    initial={false}
                    animate={{ width: `${240}px`, minWidth: `${240}px` }}
                    className={`border-r border-gray-300 whitespace-nowrap h-full block overflow-auto bg-white px-2`}>
                    {ManagedLinks.filter((r) => {
                        if (!!r.permissionCheck) {
                            return hasPermission(r.permissionCheck);
                        }
                        if (!!r.roleCheck) {
                            return hasRole(r.roleCheck);
                        }

                        return true;
                    }).map((route) => {
                        return (
                            <ActiveLink href={route.route} key={route.name}>
                                <div
                                    key={route.name}
                                    className="focus-within text-passport items-center flex justify-between pl-6 py-4 hover:bg-teal-100 rounded-md mt-1">
                                    {route.name}
                                </div>
                            </ActiveLink>
                        );
                    })}
                </motion.div>
                <motion.div
                    initial={false}
                    animate={{ left: `${240}px` }}
                    className={`absolute top-0 right-0 bottom-0 ${bgColor} overflow-auto`}>
                    <motion.div className="max-w-5xl mx-auto w-full h-full px-8">
                        <>
                            {!!breadCrumbUrl && !!breadCrumb && (
                                <div className="flex items-center text-gray-300 gap-x-1 mt-4">
                                    <NextLink href={`/users/edit`} passHref>
                                        <Text
                                            as="a"
                                            type="link"
                                            css={{ display: 'flex', alignItems: 'center' }}>
                                            Account
                                        </Text>
                                    </NextLink>
                                    <p>/</p>
                                    <NextLink href={breadCrumbUrl} passHref>
                                        <Text as="a" type="link">
                                            {breadCrumb}
                                        </Text>
                                    </NextLink>
                                    {!!breadCrumSub && (
                                        <>
                                            <p>/</p>
                                            <NextLink href={breadCrumSubUrl} passHref>
                                                <Text as="a" type="link">
                                                    {breadCrumSub}
                                                </Text>
                                            </NextLink>
                                        </>
                                    )}
                                </div>
                            )}
                            {children}
                        </>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
}
